import { useEffect, useRef, useState } from 'react';

import { Badge } from '@/shared/components/shadcn/badge';

interface CommonMediaProps {
  type: 'CAROUSEL' | 'VIDEO';
  isSponsored?: boolean;
  label?: string;
}

interface CarouselMediaProps {
  type: 'CAROUSEL';
  posterUrl: string;
}

interface VideoMediaProps {
  type: 'VIDEO';
  videoSource: string;
  posterUrl: string;
  controls?: boolean;
  showViewDetail?: boolean;
  isActive?: boolean;
  asyncVideo?: boolean; // true면 초기에 영상을 로드하지 않고 mouseHover 시점에 로드함(forcePlay=true면 무시)
  forcePlay?: boolean; // true면 자동 재생 시작 후 hover out 이후에도 반복 재생 유지
}

type CarouselPostMediaProps = CommonMediaProps & CarouselMediaProps;
type VideoPostMediaProps = CommonMediaProps & VideoMediaProps;
type PostMediaProps = CarouselPostMediaProps | VideoPostMediaProps;

function CarouselPostMedia({ posterUrl, isSponsored }: CarouselPostMediaProps) {
  return (
    <div className="relative overflow-hidden rounded-md">
      <img
        className="w-full object-cover transition-all duration-300 group-hover:scale-110"
        src={posterUrl}
        alt="Post media"
        loading="lazy"
        decoding="async"
      />
      <div className="absolute inset-0 flex items-end justify-center from-black/60 via-transparent to-transparent pb-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
        <p className="text-sm font-semibold text-white">View Details</p>
      </div>
      {isSponsored && <Badge className="absolute top-2 left-2 opacity-70">Sponsored</Badge>}
    </div>
  );
}

function VideoPostMedia({
  posterUrl,
  videoSource,
  controls,
  showViewDetail = true,
  isActive = true,
  asyncVideo,
  forcePlay,
  isSponsored,
  label,
}: VideoPostMediaProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const shouldDeferLoad = Boolean(asyncVideo && !forcePlay);
  const shouldAutoPlay = Boolean(forcePlay);

  const handleMouseEnter = () => {
    setIsHovered(true);

    if (shouldDeferLoad && !isVideoLoaded) {
      setIsVideoLoaded(true);
      return;
    }

    if (shouldAutoPlay || controls) return;

    videoRef.current?.play();
  };

  const handleMouseLeave = () => {
    setIsHovered(false);

    if (shouldAutoPlay || controls) return;

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  useEffect(() => {
    if (!shouldAutoPlay) return;
    if (!isActive) {
      videoRef.current?.pause();
      return;
    }
    if (shouldDeferLoad && !isVideoLoaded) return;

    videoRef.current?.play();
  }, [isActive, shouldAutoPlay, shouldDeferLoad, isVideoLoaded]);

  useEffect(() => {
    if (!shouldDeferLoad || !isVideoLoaded || !isHovered || controls) return;

    videoRef.current?.play();
  }, [shouldDeferLoad, isVideoLoaded, isHovered, controls]);

  if (shouldDeferLoad && !isVideoLoaded) {
    return (
      <div
        className="relative overflow-hidden rounded-md"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <img
          className="w-full object-cover transition-all duration-300 group-hover:scale-110"
          src={posterUrl}
          alt="Post media"
          loading="lazy"
          decoding="async"
          style={{ aspectRatio: '9/16' }}
        />
        {isSponsored && <Badge className="absolute top-2 left-2 opacity-70">Sponsored</Badge>}
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden rounded-md"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <video
        ref={videoRef}
        className="w-full object-cover transition-all duration-300 group-hover:scale-110"
        poster={posterUrl}
        muted
        playsInline
        loop={true}
        controls={controls}
        style={{
          aspectRatio: '9/16',
        }}
      >
        <source src={videoSource} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      {showViewDetail && !asyncVideo && (
        <div className="user-select-none pointer-events-none absolute inset-0 flex items-end justify-center bg-linear-to-t from-black/60 via-transparent to-transparent pb-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <p className="text-sm font-semibold text-white">View Details</p>
        </div>
      )}

      <div className="absolute top-2 left-2 flex gap-1 opacity-70">
        {isSponsored && <Badge>Sponsored</Badge>}
        {label && <Badge>{label}</Badge>}
      </div>
    </div>
  );
}

export function PostMedia(props: PostMediaProps) {
  switch (props.type) {
    case 'CAROUSEL':
      return <CarouselPostMedia {...props} />;
    case 'VIDEO':
      return <VideoPostMedia {...props} />;
    default:
      throw new Error('Invalid media type');
  }
}
